import { Icon, spacing, Text } from '@scality/core-ui';
import type React from 'react';
import { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { CenteredLoader } from '../../components/CenteredContent';
import { GuardianContext } from '../../components/GuardianProvider';
import { useCustomerOptions } from '../../hooks/portal/useCustomerOptions';
import { useDeploymentOptions } from '../../hooks/portal/useDeploymentOptions';
import { useInstanceLabels } from '../../hooks/portal/useInstanceLabel';
import { useInstances } from '../../hooks/useInstances';
import { DashboardExportBoard } from '../export/DashboardExportBoard';
import { exportCaption } from '../export/exportCaption';
import { useDashboardExport } from '../export/useDashboardExport';
import {
  firstControlNeedingPick,
  instanceControlKeyOf,
  selectionCapOf,
  useAppliedDashboardFilters,
  useDashboardFilters,
  useRenderedFilters,
} from '../filters';
import { useDashboardMetadataWriter } from '../hooks/useDashboardMetadata';
import { useControlOptions, useDashboardTemplates, useRenderedDashboard } from '../hooks/useDashboards';
import { useGetDashboardSummaryOnLoad } from '../hooks/useGetDashboardSummaryOnLoad';
import { toDashboardAgentState } from '../metadata/serialize';
import { type Control, CUSTOMER_DIMENSION, INSTANCE_DIMENSION } from '../types/controls';
import { DashboardAsk } from './DashboardAsk';
import { DashboardFilters } from './DashboardFilters';
import { LayoutNode } from './LayoutNode';

const EMPTY_SELECTIONS = {};
const EMPTY_SCOPE: string[] = [];

/** Header above, scrolling widgets below — the header is outside the scroll, not sticky in it. */
const Canvas = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

/** Shaped like `DashboardAsk`, so the two empty states read as one family. */
const Ask = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.r12};
  padding: ${spacing.r32} ${spacing.r16};
  text-align: center;
`;

/** The only thing that scrolls, so the scrollbar starts under the header's separator. */
const Widgets = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.r16};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing.r16};
`;

/**
 * Fetches a rendered dashboard and draws its layout tree (left column) beneath a header built
 * from the template's controls. Criteria live in the URL and are committed on Apply — including
 * the instance scope, which the analytics chat reads from there too, so applying it re-scopes the
 * chat. The header stays put while the widgets scroll.
 *
 * The template list (already loaded by the layout) carries the controls and the default range,
 * so the bar is complete and the first render correctly filtered without waiting for a
 * response — the render only adds the data.
 */
export const DashboardCanvas: React.FC<{ templateId: string }> = ({ templateId }) => {
  const { data: templates } = useDashboardTemplates();
  const summary = useMemo(() => templates?.find((template) => template.id === templateId), [templates, templateId]);
  const controls = useMemo(() => summary?.controls ?? [], [summary]);

  const applied = useAppliedDashboardFilters(controls);

  const instanceControl = useMemo(
    () => controls.find((control) => control.key === instanceControlKeyOf(controls)),
    [controls],
  );
  const shown = useRenderedFilters(applied);

  const needsDeployment = !!instanceControl && (shown?.scopedInstanceIds.length ?? 0) === 0;

  /**
   * What the render cannot do without: the instance and whatever it hangs off. A ring opens on a
   * default, so it is never required; a capped control is only once it offers more than its cap
   * (`needsPickOn` below).
   */
  const requiredControlKeys = useMemo(() => {
    if (!instanceControl) return [];
    const byKey = new Map(controls.map((control) => [control.key, control]));
    const required: string[] = [];
    const walk = (control: Control | undefined) => {
      if (!control || required.includes(control.key)) return;
      for (const parent of control.dependsOn ?? []) walk(byKey.get(parent));
      required.push(control.key);
    };
    walk(instanceControl);
    return required;
  }, [controls, instanceControl]);

  const { data, isLoading, isError, isFetching } = useRenderedDashboard(
    templateId,
    shown?.selectionsByControlKey ?? EMPTY_SELECTIONS,
    shown?.timeRangeOverride,
    !needsDeployment,
  );

  const {
    data: { instances },
  } = useInstances();
  const filters = useDashboardFilters({
    templateId,
    controls,
    templateDefaultTimeRange: summary?.defaultTimeRange ?? undefined,
    accessibleInstanceIds: instances,
  });
  const { data: optionsData, isFetching: optionsFetching } = useControlOptions(templateId, filters.stagedSelections);
  const options = optionsData?.options ?? {};

  // The empty state offers the same values the controls do. Both hooks run whatever the blocking
  // step is; each is a cache read after the first.
  const customerControlKey = controls.find((control) => control.dimension === CUSTOMER_DIMENSION)?.key;
  const askCustomers = useCustomerOptions(summary?.products);
  const askDeployments = useDeploymentOptions({
    products: summary?.products,
    customers: customerControlKey ? filters.stagedSelections[customerControlKey] : undefined,
  });

  const needsPickOn = useMemo(
    () => firstControlNeedingPick(controls, options, filters.stagedSelections),
    [controls, options, filters.stagedSelections],
  );

  /**
   * The step to act on next. Read off the staged selection, not the applied one, so picking in the
   * empty state advances the question instead of loading a dashboard nobody has finished scoping.
   */
  const blockingControl = useMemo(() => {
    const answered = (key: string) =>
      (filters.stagedSelections[key]?.length ?? 0) > 0 || (optionsData?.defaultSelections?.[key]?.length ?? 0) > 0;
    const key = requiredControlKeys.find((candidate) => !answered(candidate));
    return controls.find((control) => control.key === key) ?? needsPickOn;
  }, [controls, requiredControlKeys, filters.stagedSelections, optionsData?.defaultSelections, needsPickOn]);

  const askOptions = useMemo(() => {
    const portal =
      blockingControl?.dimension === CUSTOMER_DIMENSION
        ? askCustomers
        : blockingControl?.dimension === INSTANCE_DIMENSION
          ? askDeployments
          : undefined;
    if (!portal) return options[blockingControl?.key ?? ''] ?? [];
    return portal.items.map((item) => ({ value: item.id, label: item.label }));
  }, [blockingControl, askCustomers, askDeployments, options]);

  // Hand the current dashboard (widgets + rendered series) to the dashboard agent via
  // conversation metadata.
  const { conversationId } = useContext(GuardianContext);
  const setDashboardMetadata = useDashboardMetadataWriter(conversationId);

  useEffect(() => {
    if (data && data.id === templateId && !isFetching && !needsDeployment) {
      setDashboardMetadata(toDashboardAgentState(data, shown?.timeRangeOverride));
    }
  }, [data, templateId, isFetching, needsDeployment, shown?.timeRangeOverride, setDashboardMetadata]);

  // Once the current dashboard has rendered (its widgets are now in the metadata above),
  // greet a fresh conversation with an auto-generated summary from the dashboard agent.
  useGetDashboardSummaryOnLoad({
    conversationId,
    ready: !!data && data.id === templateId && !needsDeployment,
  });

  // Applying without a deployment renders nothing, and past a control's cap there is nothing it may
  // draw, so the button has nothing to commit. Read off the *staged* selection, not the applied
  // one: picking a deployment must arm Apply straight away.
  const instanceControlKey = instanceControlKeyOf(controls);
  const pendingPick =
    instanceControlKey && (filters.stagedSelections[instanceControlKey]?.length ?? 0) === 0
      ? 'Pick a deployment first'
      : needsPickOn
        ? `Pick up to ${selectionCapOf(needsPickOn)} ${needsPickOn.label.toLowerCase()}s first`
        : undefined;

  // Exporting captures what is on screen, so it reads what is rendered, never what the bar shows.
  const dashboardName = summary?.name ?? 'Dashboard';
  const { boardRef, isBoardMounted, exportingFormat, startExport } = useDashboardExport(dashboardName);
  const renderedElement = !needsDeployment && !isError && data ? data.element : null;
  // DeploymentControl names its picks from the portal directory, not from `options`; resolve them
  // the same way or the caption prints raw instance ids. Shared query cache, so no extra request.
  const { labelOf: labelOfInstance } = useInstanceLabels(shown?.scopedInstanceIds ?? EMPTY_SCOPE);
  const captionOptions = useMemo(
    () =>
      instanceControlKey
        ? {
            ...options,
            [instanceControlKey]: (shown?.scopedInstanceIds ?? []).map((id) => ({
              value: id,
              label: labelOfInstance(id),
            })),
          }
        : options,
    [options, instanceControlKey, shown?.scopedInstanceIds, labelOfInstance],
  );

  const caption = useMemo(
    () =>
      exportCaption({
        controls,
        selections: shown?.selectionsByControlKey ?? EMPTY_SELECTIONS,
        options: captionOptions,
        timeRange: shown?.timeRangeOverride ?? summary?.defaultTimeRange ?? undefined,
      }),
    [controls, shown?.selectionsByControlKey, captionOptions, shown?.timeRangeOverride, summary?.defaultTimeRange],
  );

  return (
    <Canvas>
      <DashboardFilters
        title={dashboardName}
        controls={controls}
        templateProducts={summary?.products}
        options={options}
        defaultSelections={optionsData?.defaultSelections}
        optionsLoading={optionsFetching}
        staged={filters.stagedSelections}
        stagedTimeRange={filters.stagedTimeRangeOverride}
        defaultTimeRange={summary?.defaultTimeRange ?? undefined}
        isDirty={filters.hasUnappliedChanges}
        pendingPick={pendingPick}
        refreshing={isFetching}
        exportingFormat={exportingFormat}
        canExport={renderedElement !== null}
        onControlChange={(control, values) => filters.setControlValueAndResetDependents(control.key, values)}
        onTimeRangeChange={filters.stageTimeRange}
        onRefresh={filters.applyStagedFilters}
        onExport={startExport}
      />
      <Widgets>
        {needsDeployment && blockingControl ? (
          <DashboardAsk
            control={blockingControl}
            options={askOptions}
            optionsLoading={optionsFetching}
            pickHint={
              blockingControl.dimension === CUSTOMER_DIMENSION
                ? ' Pick one to see their deployments.'
                : blockingControl === needsPickOn
                  ? ` Pick one to start — up to ${selectionCapOf(blockingControl)} in the ${blockingControl.label} control above.`
                  : undefined
            }
            onPick={(value) => filters.setControlValueAndResetDependents(blockingControl.key, [value])}
          />
        ) : needsDeployment ? (
          <Ask>
            <Icon name="Check" size="lg" color="statusHealthy" />
            <Text variant="Larger" color="textPrimary">
              Ready to load
            </Text>
            <Text variant="Basic" color="textSecondary">
              Adjust the filters above if you need to, then press Apply.
            </Text>
          </Ask>
        ) : isLoading ? (
          <CenteredLoader />
        ) : isError || !data ? (
          <Text color="statusCritical">Failed to load dashboard.</Text>
        ) : (
          <LayoutNode node={data.element} />
        )}
      </Widgets>
      {isBoardMounted && renderedElement ? (
        <DashboardExportBoard ref={boardRef} title={dashboardName} caption={caption} element={renderedElement} />
      ) : null}
    </Canvas>
  );
};
